AJAXRPC = require './ajax-rpc'
ApiError = require 'network/api-error'
Auth = require 'db/auth'
Backbone = require 'backbone'
Batcher = require 'network/rpc/batcher'
Promise = require 'bluebird'
Socket = require 'network/socket'
SocketRPC = require './socket-rpc'
Storage = require 'lib/storage'
Util = require 'lib/util'
_ = require 'underscore'
log = require 'debug/log'
track = require 'debug/track'

window.dropTheHammer = ->
  Storage.set('useWebsockets', 'yes')
  console?.log?("Websockets engaged! Reload the page, please. Use pitStop() to turn sockets off.")

window.pitStop = ->
  Storage.set('useWebsockets', 'no')
  console?.log?("Shutting Websockets down! Reload the page, please. Use dropTheHammer() to turn sockets back on.")

VERIFY_INTERVAL_MS = 20 * 1000

# We want to immediately open up an rpc channel, so we use AJAX until the socket has connected
# and proven that it can answer a ping. If the socket connects and answers quickly, we use
# that for all future requests. If not, we just close it down and keep using AJAX. We defer
# setting up anything that will require polling on the AJAX rpc until we either confirm or
# give up on the socket rpc.
class RPC
  _.extend @prototype, Backbone.Events

  constructor: ->
    @rpcSocket = @rpcAJAX = @rpcCurrent = null
    @currentSubscriptions = {}

    @rpcConnectTime = 0
    @socketRetryWaitTime = 0

    # Always start an RPC if we're logged in, so we can get our notifications
    @handleLogin()

    setTimeout =>
      @verifySubscriptions()
    , VERIFY_INTERVAL_MS
    return

  rpcReady: ->
    if !@isUsingSocket()
      @rpcCurrent.poll()

    @trigger('ready')

    @resubscribe()

    @listenTo @, 'reconnect', =>
      @resubscribe()

  resubscribe: ->
    @rpcCurrent.resync()

    for idModel, subscription of @currentSubscriptions
      @subscribe(subscription.modelType, idModel, subscription.tags)

  setActiveRPC: (rpc) ->
    @stopListening @rpcCurrent
    @rpcCurrent = rpc

    @listenTo @rpcCurrent, 'all', (args...) =>
      @trigger(args...)
    return

  lazyCreateRPC: ->
    if @isUsingSocket() || ((Util.getTime() - @rpcConnectTime) < @socketRetryWaitTime)
      return

    @rpcConnectTime = Util.getTime()
    @socketRetryWaitTime = Util.randomWait(Util.getMs(minutes: 20), Util.getMs(minutes: 10))

    # Create a new socket instance to force new Socket creation
    @rpcSocket = new SocketRPC(@)
    if @rpcAJAX?
      @rpcAJAX.stopPolling()
    else
      @rpcAJAX = new AJAXRPC(@)

    # Until the socket proves it works, you're on AJAX rpc.
    @setActiveRPC(@rpcAJAX)

    if (@rpcSocket.hasSocketTransport())
      # Sockets have a chance of working. They have 8s to answer a ping before we give up on 'em.
      msPingWait = 8000

      @rpcSocket.ping (err, result) =>
        pingElapsedTime = Util.getTime() - @rpcConnectTime
        if err? || pingElapsedTime > msPingWait
          # Socket took too long. Use AJAX.
          log('rpc', "Socket not responsive. Using AJAX.")
          @rpcSocket.disconnect()
          @rpcSocket.destroy()
          @rpcSocket = null
          track("RPC", "No Sockets", "Slow ping", pingElapsedTime)
          setTimeout =>
            @lazyCreateRPC()
          , @socketRetryWaitTime
        else
          # Yay, we have sockets!
          log('rpc', "Socket responded to ping in a timely fashion. Using sockets.")
          @setActiveRPC(@rpcSocket)
          @rpcReady()
        return
    else
      # We do not have an acceptable socket transport. Use AJAX.
      log('rpc', "No socket connect. Using AJAX.")
      @rpcReady()

  handleLogin: ->
    if Auth.isLoggedIn()
      @lazyCreateRPC()

  unsubscribe: (modelType, idModel) ->
    @lazyCreateRPC()
    @rpcCurrent.unsubscribe(modelType, idModel)
    delete @currentSubscriptions[idModel]

  _subscribeOrHandle: (modelType, idModel, tags) ->
    Promise.fromNode (next) =>
      @rpcCurrent.subscribe(modelType, idModel, tags, next)
    .catch (err) =>
      @handleInvalidSubscription(idModel)

      if err.message == 'unauthorized'
        @trigger('subscription_unauthorized', modelType, idModel, tags)

  subscribe: (modelType, idModel, tags) ->
    @lazyCreateRPC()

    if (subscription = @currentSubscriptions[idModel])?
      subscription.tags = _.union(subscription.tags, tags)
    else
      @currentSubscriptions[idModel] =
        tags: tags
        ixLastUpdate: -1
        modelType: modelType
        idModel: idModel

    @_subscribeOrHandle(modelType, idModel, tags)

  getIxLastUpdate: (idModel) ->
    return @currentSubscriptions[idModel]?.ixLastUpdate

  setIxLastUpdate: (idModel, ixLastUpdate) ->
    if (entry = @currentSubscriptions[idModel])?
      entry.ixLastUpdate = ixLastUpdate

  resync: (idModels...) ->
    @lazyCreateRPC()
    @rpcCurrent.resync?(idModels...)

  isUsingSocket: -> @rpcCurrent? && @rpcCurrent == @rpcSocket

  handleInvalidSubscription: (idModelInvalid) ->
    if (subscription = @currentSubscriptions[idModelInvalid])?
      { modelType, idModel, tags } = subscription

      @unsubscribe(modelType, idModel)
      @trigger('invalidModel', modelType, idModel, tags)
    return

  _verificationUrls: ->
    subscriptions = Object.values(@currentSubscriptions)

    me = Auth.me()

    subscriptions.map ({ modelType, idModel }) ->
      url =
        switch modelType
          when 'Board'
            if me && idModel in me.get('idBoards')
              # If it's in our idBoards we're allowed to see it
              null
            else
              "/boards/#{ idModel }/name"
          when 'Member'
            if idModel == Auth.myId()
              # Don't bother checking if we can read our member channel
              null
            else
              "/members/#{ idModel }/fullName"
          when 'Organization'
            if me && idModel in me.get('idOrganizations')
              # If it's in our idOrganizations we know we're allowed to see it
              null
            else
              "/organizations/#{ idModel }/displayName"
          else
            null

      if url
        { id: idModel, url }
    .filter (entry) -> entry?

  _sendVerificationRequests: ->
    verifyData = @_verificationUrls()

    if verifyData.length == 0
      return Promise.resolve()

    batcher = new Batcher()

    verifyData.forEach ({ id, url }) =>
      batcher.request(url)
      .catch ApiError, =>
        @handleInvalidSubscription(id)

    batcher.send()

  # This is meant to catch some tricky situations where we might have lost
  # access to something even though we received no websocket update informing
  # us.  For example, a public board or team that we're looking at may have
  # been deleted, or a team visible board that we aren't a member of might
  # have been changed to private.
  verifySubscriptions: ->
    clearTimeout(@_verifyTimeout)

    @_sendVerificationRequests()
    .finally =>
      timeout = @_verifyTimeout = setTimeout =>
        if timeout == @_verifyTimeout
          requestAnimationFrame =>
            @verifySubscriptions()
      , VERIFY_INTERVAL_MS
    .done()

module.exports = rpc = window.rpc = new RPC()

# If we're unable to subscribe to our own member channel, something
# has gone wrong (possibly we deleted our account or our session was
# invalidated from another browser)
if Auth.isLoggedIn()
  rpc.on 'invalidModel', (typeName, id) ->
    if typeName == 'Member' && Auth.isMe(id)
      Auth.removeToken()




// WEBPACK FOOTER //
// ./app/scripts/network/rpc/index.coffee